import type { ScoreStats } from "../engine/scoring";

// Score history. Persisted to localStorage so results survive across sessions
// on the same rig.

/** One completed scored run, as stored and shown in the stats panel. */
export interface ScoreRecord {
  songId: string;
  title: string;
  /** ISO timestamp of completion. */
  date: string;
  perfect: number;
  good: number;
  misses: number;
  wrong: number;
  /** perfect + good + misses (notes that reached the line). */
  total: number;
  /** hits / total, 0–1. */
  accuracy: number;
  /** Mean |timing error| over hits, in ms. */
  avgErrorMs: number;
}

const KEY = "drop-notes.scores";
const MAX_RECORDS = 200;

/** Build a record from a finished run's stats. */
export function recordFromStats(
  songId: string,
  title: string,
  date: string,
  stats: ScoreStats,
): ScoreRecord {
  const hits = stats.perfect + stats.good;
  const total = hits + stats.misses;
  return {
    songId,
    title,
    date,
    perfect: stats.perfect,
    good: stats.good,
    misses: stats.misses,
    wrong: stats.wrong,
    total,
    accuracy: total > 0 ? hits / total : 0,
    avgErrorMs: hits > 0 ? (stats.sumAbsError / hits) * 1000 : 0,
  };
}

/** Per-song rollup of the stored runs, for the Results view. */
interface SongAggregate {
  songId: string;
  title: string;
  runs: number;
  /** Most recent run's accuracy, 0–1. */
  latestAccuracy: number;
  /** Median accuracy across all runs, 0–1. */
  medianAccuracy: number;
  /** Best (high-score) accuracy, 0–1. */
  bestAccuracy: number;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] ?? 0;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

/**
 * Roll up runs per song into latest / median / best accuracy. Expects records
 * newest-first (as {@link loadScores} returns them); the result keeps that
 * recency order, so the most recently played song is first.
 */
export function aggregateScores(records: ScoreRecord[]): SongAggregate[] {
  const order: string[] = [];
  const groups = new Map<string, ScoreRecord[]>();
  for (const r of records) {
    const list = groups.get(r.songId);
    if (list) {
      list.push(r);
    } else {
      groups.set(r.songId, [r]);
      order.push(r.songId);
    }
  }
  return order.map((id) => {
    const list = groups.get(id) ?? [];
    const latest = list[0];
    const accuracies = list.map((r) => r.accuracy);
    return {
      songId: id,
      title: latest?.title ?? id,
      runs: list.length,
      latestAccuracy: latest?.accuracy ?? 0,
      medianAccuracy: median(accuracies),
      bestAccuracy: accuracies.reduce((m, a) => Math.max(m, a), 0),
    };
  });
}

/** All stored runs, newest first. */
export function loadScores(): ScoreRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ScoreRecord[];
    if (!Array.isArray(parsed)) return [];
    // Drop tampered/garbage records so NaN never reaches the aggregates.
    return parsed.filter(
      (r) =>
        r &&
        typeof r.songId === "string" &&
        Number.isFinite(r.accuracy) &&
        Number.isFinite(r.total),
    );
  } catch {
    return [];
  }
}

/** Prepend a run and persist (capped to the most recent runs). */
export function saveScore(record: ScoreRecord): void {
  try {
    const next = [record, ...loadScores()].slice(0, MAX_RECORDS);
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // ignore — stats simply won't persist
  }
}

/** Forget all stored runs. */
export function clearScores(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
